<template>
    <div>
        <hts-form-radio
            v-model="dataEdit.createPriceFrom"
            :label="$t('labels.createPriceFrom')"
            :items="createPriceFromOptions()"
            readonly
            value-key="value"
            label-key="label"
            class="mb-2" />
        <hts-card :title="$t('labels.fleetVehicle')">
            <hts-select-quote
                v-if="dataEdit.createPriceFrom === 1"
                v-model="dataEdit.quote"
                v-model:text="dataEdit.quoteCode"
                name="quote"
                prop="quote"
                readonly />

            <hts-select-quote-request
                v-if="dataEdit.createPriceFrom === 2"
                v-model="dataEdit.quoteRequest"
                v-model:text="dataEdit.quoteRequestCode"
                name="quoteRequest"
                prop="quoteRequest"
                readonly />

            <div class="grid gap-x-2 grid-cols-4">
                <hts-select-cooperator
                    v-model="dataEdit.vendor"
                    v-model:name="dataEdit.vendorName"
                    :label="$t('labels.nameFleetVehicle')"
                    type-cooperator="CUSTOMER"
                    :creatable="true"
                    readonly
                    prop="vendor"
                    name-select="fleetVehicle" />
                <hts-select-cooperator-contact
                    v-model="dataEdit.contact"
                    v-model:name="dataEdit.contactName"
                    readonly
                    :cooperator-id="dataEdit.vendor"
                    :label="$t('labels.vendorPriceModule.contact')"
                    prop="contact"
                    name-select="vendorPriceModule.contact"
                    @change="(contact) => selectContact(contact)" />
                <hts-form-input
                    v-model="dataEdit.phone"
                    :label="$t('labels.phone')"
                    readonly />
                <hts-form-input
                    v-model="dataEdit.email"
                    :label="$t('labels.email')"
                    readonly />
            </div>
        </hts-card>

        <hts-card :title="$t('labels.vendorPriceModule.priceInfo')">
            <div class="w-full">
                <hts-select-work-flow
                    v-model="dataEdit.workflow"
                    v-model:name="dataEdit.workflowName"
                    prop="workflow"
                    readonly
                    @change="selectWorkflow" />
                <hts-workflow-position
                    :positions="dataEdit.positions"
                    :same-positions="dataEdit.theSamePositions"
                    :is-active="true">
                    <template #default="{ item, index }">
                        <span>
                            {{ item.nodeTypeName }}
                        </span>
                        <hts-form-select
                            v-model="dataEdit.positions[index].quoteEqual"
                            readonly
                            class="mr-2"
                            :label="$t('labels.quoteEqual')"
                            :items="listSelectQuoteEqual"
                            :prop="'quoteEquals' + index" />
                    </template>
                </hts-workflow-position>
                <div
                    v-if="dataEdit.positions.length > 0"
                    v-loading="loadingSelectNode">
                    <span class="text-base block px-0 font-medium text-default-500">
                        {{ $t('labels.route') }}
                    </span>
                    <div
                        v-for="(item, index) in dataEdit.positions"
                        :key="index"
                        class="pl-10">
                        <hts-select-node
                            v-model="dataEdit.positions[index].node"
                            v-model:name="dataEdit.positions[index].fullNameNode"
                            :node-type-id="item.nodeType"
                            :prop="item.nodeType + index"
                            :label="item.nodeTypeName"
                            :businesses="item.businesses"
                            readonly
                            :location-type="item.quoteEqual"
                            :key-label="getKeyLabel(item.quoteEqual)"
                            :key-sub-label="getKeySubLabel(item.quoteEqual)"
                            @change="(val) => handelChangeSelectNode(index, val)" />
                    </div>
                    <div v-if="dataEdit.transportation">
                        <div v-if="dataEdit.transportation === 'CONTAINER'">
                            <hts-form-select
                                v-model="dataEdit.contCategory"
                                :label="$t('labels.containerType')"
                                :filterable="true"
                                :clearable="true"
                                readonly
                                :items="dataSelectWorkFlowData"
                                key-id="value"
                                key-label="label"
                                prop="workflowData"
                                name="workflowData"
                                :color="props.color" />
                            <div class="flex">
                                <hts-form-checkbox
                                    v-if="dataEdit.contCategory === 20"
                                    v-model="dataEdit.mergeContainer"
                                    color="secondary"
                                    :items="[{ label: $t('labels.mergeContainer'), value: false }]"
                                    readonly />
                                <hts-form-checkbox
                                    v-model="dataEdit.headContainer"
                                    color="secondary"
                                    :items="[{ label: $t('labels.headContainer'), value: false }]"
                                    readonly />
                            </div>
                        </div>
                        <div v-else>
                            <hts-select-category-vehicle
                                v-model="dataEdit.vehicleCategory"
                                v-model:name="dataEdit.vehicleCategoryName"
                                readonly
                                prop="workflowData" />
                        </div>
                    </div>
                    <div class="block mt-5">
                        <div class="inline-block align-middle">
                            <span>
                                {{ $t('labels.maxTaskCompletionTime') }}
                            </span>
                            <span class="text-error-500">
                                *
                            </span>
                        </div>

                        <div
                            class="tooltip inline-block align-middle"
                            :data-tip="$t('labels.warningTaskQuoteRequest')">
                            <hi-information-circle-icon class="flex items-center text-green-500 w-6 h-6" />
                        </div>
                    </div>
                    <div class="block mb-5 ml-8">
                        <div
                            v-for="(item, index) in dataEdit.tasks"
                            :key="index"
                            class="grid grid-cols-2 gap-4">
                            <hts-form-select
                                v-model="dataEdit.tasks[index].code"
                                readonly
                                :items="taskItems"
                                key-id="code"
                                key-label="taskName"
                                key-sub-label="businessName"
                                key-disabled="disabled"
                                name="task"
                                :prop="'task' + index"
                                @change="handleChangeSelectedTask" />
                            <div class="flex justify-center items-center">
                                <hts-form-input
                                    v-model="dataEdit.tasks[index].value"
                                    type="number"
                                    :items="taskItems"
                                    name="completionTime"
                                    class="flex-1"
                                    :prop="'time' + index"
                                    readonly>
                                    <template #suffix>
                                        <span class="capitalize">
                                            {{ $t('labels.hour') }}
                                        </span>
                                    </template>
                                </hts-form-input>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="grid gap-x-2 grid-cols-2">
                <hts-form-date
                    v-model="dataEdit.effectiveFromDate"
                    :label="$t('labels.vendorPriceModule.effectiveFromDate')"
                    type="date"
                    readonly
                    prop="vendorPriceModule.effectiveFromDate"
                    name="vendorPriceModule.effectiveFromDate" />
                <hts-form-date
                    v-model="dataEdit.effectiveToDate"
                    :label="$t('labels.effectiveToDateDescription')"
                    type="date"
                    readonly
                    prop="vendorPriceModule.effectiveToDate"
                    name="vendorPriceModule.effectiveToDate" />
                <hts-form-select
                    v-model="dataEdit.typeCommodity"
                    :label="$t('labels.goodsTypes')"
                    readonly
                    :items="cTypeCommodity || []"
                    multiple
                    key-id="key"
                    key-label="title"
                    :color="color"
                    name="goodsTypes"
                    prop="commodityType" />
                <hts-form-input
                    v-model="dataEdit.weightCommodity"
                    :label="$t('labels.weightCommodityByKGMaximum')"
                    type="number"
                    readonly
                    prop="weightCommodityByKGMaximum"
                    name="weightCommodityByKGMaximum" />
            </div>
            <div>
                <hts-form-textarea
                    v-model="dataEdit.description"
                    :label="$t('labels.description')"
                    readonly />
            </div>
        </hts-card>
        <hts-card :title="$t('labels.priceInfo')">
            <div class="grid gap-x-2 grid-cols-2">
                <hts-form-input
                    v-model="dataEdit.price"
                    :label="$t('labels.price')"
                    readonly
                    type="number" />
                <hts-form-input
                    v-model="dataEdit.oilPrice"
                    :label="$t('labels.oilPriceNotVat')"
                    type="number"
                    readonly />
            </div>
        </hts-card>
        <template v-if="dataEdit.createPriceFrom === 1 && dataEdit.acceptPriceOfflineFee && dataEdit.acceptPriceOfflineFee.length">
            <h4 class="mt-5">
                {{ $t('labels.priceOfflineFee') }}
            </h4>
            <hts-data-table
                ref="dataTables"
                :headers="headers"
                :single-click-edit="true"
                :items="dataEdit.acceptPriceOfflineFee"
                :suppress-row-click-selection="true"
                :is-cell-clicked="false" />
        </template>
        <map-position
            ref="mapPosition"
            key-map-store="priceVendor"
            @applyPosition="handleApplyPositionMap" />
    </div>
</template>

<script lang="ts" setup>
import MapPosition from '../../../partner/[idpartner]/business-opportunity/-components/position.vue'
import { createPriceFromOptions, IVendorPrice } from '../ignore/data'
import { ruleAddVendorPrices } from '../ignore/form'
import { $api, $lodash } from '#imports'

const { $disableReactive } = useNuxtApp()
const store = $store()

const props = defineProps({
  color: {
    type: String,
    default: 'theme'
  },
  dataEdit: {
    type: Object as PropType<IVendorPrice>,
    required: true
  }
})

const { dataEdit } = toRefs(props)
const popover = ref()
const { $formatters } = useNuxtApp()

const headers = [
  {
    field: 'positions',
    headerName: $t('labels.route'),
    sortable: true,
    valueGetter: (params: any) => {
      const pluckData = $lodash.map(params.data?.positions, 'fullNameNode')
      return $lodash.join(pluckData, ' >> ')
    }
  },
  {
    field: 'pricingEstimate.totalOfflineFee',
    headerName: $t('labels.priceOfflineFee'),
    sortable: true,
    valueGetter: (params: any) => {
      return $formatters.formatCurrency(params.data.total || 0)
    }
  }
]

const selectContact = (contact: any) => {
  if (Array.isArray(contact) && contact.length) {
    dataEdit.value.contactName = contact[0]?.fullName || ''
    dataEdit.value.email = contact[0]?.email || ''
    dataEdit.value.phone = contact[0]?.phone || ''
    return
  }

  dataEdit.value.contactName = contact?.fullName || ''
  dataEdit.value.email = contact?.email || ''
  dataEdit.value.phone = contact?.phone || ''
}

const dataWorkflow = ref<{ [key: string]: any }>([])
const selectWorkflow = (workflow: any) => {
  if (Array.isArray(workflow) && workflow.length) {
    dataWorkflow.value = workflow[0]
  } else {
    dataWorkflow.value = []
  }
  setDataHandleSelectWorkflow()
}

const setDataHandleSelectWorkflow = () => {
  dataEdit.value.positions = dataWorkflow.value.positions || []
  dataEdit.value.transportation = dataWorkflow.value.transportation || ''
  dataEdit.value.theSamePositions = dataWorkflow.value.theSamePositions || ''
  dataEdit.value.vehicleCategory = null
  dataEdit.value.vehicleCategoryName = ''
  dataEdit.value.contCategory = null
  dataEdit.value.tasks = [
    {
      code: '',
      value: ''
    }
  ]
  setDataPosition()
}
const setDataPosition = () => {
  dataEdit.value.positions.forEach((position: { [key: string]: any }, index: number) => {
    if (['Cảng', 'DEPOT', 'ICD'].includes(position.nodeTypeName)) {
      dataEdit.value.positions[index].quoteEqual = 'NODE'
    } else {
      dataEdit.value.positions[index].quoteEqual = 'WARD'
    }
  })
}

const taskData: { [key: string]: any } = computed(() => {
  return store.data.getTasks
})

const configCont: { [key: string]: any } = computed(() => store.globalSettings.getGlobalSettings.configCont)
const configVehicle: { [key: string]: any } = computed(() => {
  return store.data.getVehicleCategories
})

const CRules = computed(() => {
  const rules = { ...ruleAddVendorPrices } as { [key: string]: string }
  if (dataEdit.value.effectiveFromDate) {
    const date = dataEdit.value.effectiveFromDate ? new Date(dataEdit.value.effectiveFromDate) : ''
    if (date && dataEdit.value.effectiveToDate) {
      date.setDate(date.getDate() + 1)
      rules['vendorPriceModule.effectiveToDate'] = rules['vendorPriceModule.effectiveToDate'] + `|min_date:${date.toISOString().split('T')[0]}`
    }
  }
  dataEdit.value.tasks.forEach((task, index) => {
    const dataTask: { [key: string]: any } = taskData.value.find((iTask: { [key: string]: any }) => iTask._id === task.task)
    if (dataTask && dataTask.completionTimes) {
      const keyCompletionTime = dataEdit.value.transportation === 'CONTAINER' ? 'contCategory' : 'vehicleCategory'
      rules['time' + index] = `min_value:${
        dataTask.completionTimes &&
        dataTask.completionTimes[keyCompletionTime][dataEdit.value[keyCompletionTime] || ''] &&
        dataTask.completionTimes[keyCompletionTime][dataEdit.value[keyCompletionTime] || ''].minimumTime
          ? dataTask.completionTimes[keyCompletionTime][dataEdit.value[keyCompletionTime] || ''].minimumTime
          : 0
      }`
    }
  })
  if (dataEdit.value.transportation === 'CONTAINER' && dataEdit.value.contCategory) {
    if (configCont.value && configCont.value[dataEdit.value.contCategory] && configCont.value[dataEdit.value.contCategory].weightCargo) {
      rules.weightCommodityByKGMaximum = 'required|max_value:' + configCont.value[dataEdit.value.contCategory].weightCargo
    }
  } else if (dataEdit.value.transportation === 'NON_CONTAINER' && dataEdit.value.vehicleCategory && configVehicle.value) {
    const findVehicleCategory = configVehicle.value.find((findVehicleCategory: { [key: string]: any }) => {
      return findVehicleCategory._id === dataEdit.value.vehicleCategory
    })
    if (findVehicleCategory) {
      rules.weightCommodityByKGMaximum = `required|min_value:${findVehicleCategory.from || 0}|lt:${findVehicleCategory.to || '999999999999999'}`
    }
  }
  return rules
})

const loadingSelectNode = ref(false)

const handelChangeSelectNode = async (indexRoute: number, val: Array<{ [key: string]: any }>) => {
  loadingSelectNode.value = true
  const nodeTheSame: Array<number> | undefined = getNodeTheSamePosition(indexRoute, $disableReactive(dataEdit.value.theSamePositions))
  if (Array.isArray(val) && val.length && typeof val[0].location === 'string') {
    let dataPosition: { [key: string]: any } = {}
    const api = $urlAdmin.node.location.detail
    const res = await $api(api, { uriParams: { ID: val[0].location } })
    const resData: any = res?.data?.value
    if (resData?.success) {
      dataPosition = resData.data
    }
    if (dataPosition) {
      if (Array.isArray(nodeTheSame)) {
        nodeTheSame.forEach((numberRoute: number) => {
          if (numberRoute !== indexRoute) {
            if (Array.isArray(dataPosition.nodeDetail) && dataPosition.nodeDetail.length) {
              const findItemPosition = dataPosition.nodeDetail.find((itemNode: { [key: string]: any }) => {
                return itemNode.nodeType && itemNode.nodeType.name === dataEdit.value.positions[indexRoute].nodeTypeName
              })
              if (findItemPosition) {
                dataEdit.value.positions[numberRoute].node = findItemPosition._id
                dataEdit.value.positions[numberRoute].nodeFullName = findItemPosition.fullNameNode
              }
            }
          }
        })
      }
    }
  } else {
    nodeTheSame &&
      nodeTheSame.forEach((numberRoute: number) => {
        if (numberRoute !== indexRoute) {
          dataEdit.value.positions[numberRoute].node = ''
          dataEdit.value.positions[numberRoute].nodeFullName = ''
        }
      })
  }
  loadingSelectNode.value = false
}

const listSelectQuoteEqual = [
  {
    value: 'NODE',
    label: $t('labels.node')
  },
  {
    value: 'WARD',
    label: $t('labels.ward')
  },
  {
    value: 'DISTRICT',
    label: $t('labels.district')
  },
  {
    value: 'CITY',
    label: $t('labels.city')
  }
]

const dataSelectWorkFlowData = [
  {
    value: 20,
    label: $t('labels.vehicleModule.20ft')
  },
  {
    value: 40,
    label: $t('labels.vehicleModule.40ft')
  },
  {
    value: 45,
    label: $t('labels.vehicleModule.45ft')
  }
]

const handleChangeSelectedTask = (data: any) => {
  if (Array.isArray(data) && data.length) {
    const indexData = dataEdit.value.tasks.findIndex((task: { [key: string]: any }) => {
      return task.code === data[0].code
    })
    if (indexData !== -1) {
      dataEdit.value.tasks[indexData] = { ...dataEdit.value.tasks[indexData], ...data[0] }
    }
  }
}

const taskItems = computed(() => {
  const result: Array<{ [key: string]: any }> = []
  if (Array.isArray(dataEdit.value.positions)) {
    dataEdit.value.positions.forEach((positions: { [key: string]: any }) => {
      if (Array.isArray(positions.tasks)) {
        positions.tasks.forEach((task: { [key: string]: any }) => {
          const index = result.findIndex((i) => i.key === task.key)
          if (index === -1 && task.responsibleForImplementation === 'CUSTOMER') {
            task.nodeType = positions.nodeType || ''
            task.node = positions.node || ''
            task.index = positions.index || ''
            task.nodeTypeName = positions.nodeTypeName || ''
            task.code = (positions.nodeType || '') + '-' + task.key
            const findIndexSelected = dataEdit.value.tasks.findIndex((iTask) => iTask.code === task.code)
            task.disabled = false
            if (findIndexSelected !== -1) {
              task.disabled = true
            }
            result.push(task)
          }
        })
      }
    })
  }
  return result
})

const getNodeTheSamePosition = (index: number, data: Array<Array<number>>) => {
  if (typeof index !== 'number' || !Array.isArray(data)) {
    return
  }
  let result: Array<number> = []
  const filteredItems = data.filter((i: Array<number>) => i.includes(index))
  const tempData = data.filter((i: Array<number>) => !i.includes(index))
  filteredItems.forEach((item) => {
    if (!Array.isArray(item) || item.length !== 2) {
      return
    }
    const tempIndex = item.findIndex((o) => o === index)
    let tempIndex2
    if (tempIndex === 0) {
      tempIndex2 = 1
    } else {
      tempIndex2 = 0
    }

    result.push(item[1])
    result.push(item[0])

    const itemNodeTheSames: Array<number> | undefined = getNodeTheSamePosition(item[tempIndex2], tempData)
    if (Array.isArray(itemNodeTheSames)) {
      result = result.concat(itemNodeTheSames)
    }
  })
  return $lodash.uniq(result)
}
const mapPosition = ref()

const openWithMap = () => {
  mapPosition.value && mapPosition.value.open($disableReactive(dataEdit.value.positions), $disableReactive(dataEdit.value.theSamePositions))
}

const handleApplyPositionMap = (positions: Array<{ [key: string]: any }>) => {
  dataEdit.value.positions = $disableReactive(positions)
}

const cTypeCommodity = computed(() => {
  const result: Array<{ [key: string]: any }> = $disableReactive(typeCommodity)
  if (dataEdit.value.typeCommodity && dataEdit.value.typeCommodity.length === 1 && dataEdit.value.typeCommodity.includes('NORMAL')) {
    result.forEach((i: { [key: string]: any }, index: number) => {
      if (i.key !== 'NORMAL') {
        result[index].disabled = true
      }
    })
  } else if (dataEdit.value.typeCommodity && dataEdit.value.typeCommodity.length && !dataEdit.value.typeCommodity.includes('NORMAL')) {
    result[0].disabled = true
  }
  return result
})

const typeCommodity = [
  {
    key: 'NORMAL',
    title: $t('labels.ordinaryGoods')
  },
  {
    key: 'LIQUID',
    title: $t('labels.liquid')
  },
  {
    key: 'GAS',
    title: $t('labels.gas')
  },
  {
    key: 'FROZEN',
    title: $t('labels.frozen')
  },
  {
    key: 'DANGEROUS',
    title: $t('labels.dangerousGoods')
  },
  {
    key: 'FRAGILE',
    title: $t('labels.fragileGoods')
  },
  {
    key: 'OVERWEIGHT',
    title: $t('labels.overweightCargo')
  }
]

const getKeySubLabel = (locationType: string) => {
  if (locationType === 'NODE') {
    return 'address'
  }
  return 'address'
}
const getKeyLabel = (locationType: string) => {
  if (locationType === 'NODE') {
    return 'name'
  }
  return 'addressByType'
}
</script>
